import { ApolloQueryResult, useApolloClient } from '@graphcommerce/graphql'
import { SectionContainer } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { Box, Button, Modal, Paper, Skeleton } from '@mui/material'
import { useCallback, useState } from 'react'
import { StandardButton } from '../../Button/ButtonStyles'
import { SectionTitle } from '../../Container/SectionTitle'
import { AlertPrompt } from '../../Prompt/AlertPrompt'
import { ConfirmationPrompt } from '../../Prompt/ConfirmationPrompt'
import { UserSkeleton } from '../../Skeleton/UserSkeleton'
import { Spinner } from '../../Spinner'
import { DeleteSubUserDocument } from '../SubUserForm/DeleteSubUser.gql'
import { SendEmailSubUserDocument } from '../SubUserForm/SendEmailSubUser.gql'
import { SubUserForm } from '../SubUserForm/SubUserForm'
import { SubUserFragment } from './SubUser.gql'
import { SubUsersQuery } from './SubUsers.gql'

export type SubUsersProps = {
  users?: SubUserFragment[]
} & {
  loading: boolean
  refetch: () => Promise<ApolloQueryResult<SubUsersQuery>>
}

export function SubUsers(props: SubUsersProps) {
  const { users = [], loading, refetch } = props
  const client = useApolloClient()
  const [selectedRole, setSelectedRole] = useState<null | string | number>(null)
  const [showForm, setShowForm] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean | number>(false)
  const [isSendingEmail, setIsSendingEmail] = useState<boolean | number>(false)
  const [displayPrompt, setDisplayPrompt] = useState<boolean>(false)
  const [subUserEmail, setSubUserEmail] = useState<string>('')
  const [displayWarning, setIsDisplayWarning] = useState<boolean>(false)
  const [responseDelete, setResponseDelete] = useState<boolean | undefined>()
  const selectedUser = selectedRole ? users.find((user) => user.sub_id === selectedRole) : false

  const handleClosePrompt = () => {
    setDisplayPrompt(false)
  }

  const handleEditUser = (userId: number) => {
    setSelectedRole(userId)
    setShowForm(true)
  }

  const handleResendEmail = () => {}

  const handleSubUserDelete = useCallback(
    async (response: boolean, userId: number) => {
      if (!response) return

      try {
        setIsDeleting(userId)
        await client.mutate({
          mutation: DeleteSubUserDocument,
          variables: {
            userId,
          },
        })
        setIsDeleting(false)
        refetch()
          .then(() => {})
          .catch(() => {})
      } catch (error) {
        console.log('error', error)
        setIsDeleting(false)
      }
    },
    [client, refetch],
  )

  const handleSubUserSendEmail = useCallback(
    async (userId: number, userEmail?: string, shouldRefetch = true) => {
      try {
        if (!userId || !userEmail) return
        setIsSendingEmail(userId)
        setSubUserEmail(userEmail)
        if (userEmail) {
          await client
            .mutate({
              mutation: SendEmailSubUserDocument,
              variables: {
                email: userEmail,
              },
            })
            .then((result) => setDisplayPrompt(true))
          setIsSendingEmail(false)
          if (shouldRefetch) {
            refetch()
              .then(() => {})
              .catch(() => {})
          }
        }
      } catch (error) {
        setIsSendingEmail(false)
      }
    },
    [client, refetch, setIsSendingEmail],
  )

  if (loading) {
    return (
      <div className='w-full'>
        <Paper elevation={3} className='md:px-15 p-10 md:py-8'>
          <span className='Type-XXL-Regular w-full'>Manage users</span>
          <hr className='mb-10 mt-2 text-20-grey' />
          <UserSkeleton />
        </Paper>
      </div>
    )
  }

  const handleCloseSlideInModal = () => {}

  return (
    <Paper elevation={3} className='md:px-15 w-full p-10 md:w-[80%] md:py-8'>
      <span className='Type-XXL-Regular w-full'>Manage users</span>
      <hr className='mb-10 mt-2 text-20-grey' />
      <div>
        {users && users.length >= 1 && (
          <div className='flex flex-col'>
            {users?.map((user) => {
              if (user && user.sub_id) {
                return (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                  <div className='flex min-h-[40px] flex-col items-center justify-between gap-5 border-b-[1px] border-b-20-grey py-[10px] md:flex-row'>
                    <div className='flex w-full items-center justify-start gap-2 md:w-auto md:justify-center md:gap-5'>
                      <div
                        key={user.sub_id}
                        className='Type-XL-Regular hover:Type-XL-Bold flex h-[40px] w-[40px] items-center justify-center rounded-[50%] bg-20-grey p-[10px] text-pure-white
                      '
                      >
                        <span className=''>{`${user?.sub_name?.trimStart().charAt(0)}`}</span>
                        {(isDeleting === user.sub_id || isSendingEmail === user.sub_id) && (
                          <Spinner />
                        )}
                      </div>
                      <div className='Type-XL-Regular flex flex-col items-start justify-center gap-2 capitalize'>
                        <span>{user?.sub_name}</span>
                        <span className='text-50-grey'>{user?.role?.role_name}</span>
                      </div>
                    </div>
                    <div className='grid w-full grid-cols-2 items-center justify-center gap-5 md:flex md:w-auto'>
                      <Button
                        color='primary'
                        className='w-full max-w-[350px] bg-pure-white uppercase md:w-auto'
                        variant='outlined'
                        onClick={() => handleEditUser(Number(user.sub_id))}
                      >
                        {i18n._('Edit User')}
                      </Button>
                      <Button
                        color='primary'
                        className='w-full max-w-[350px] whitespace-nowrap bg-pure-white uppercase md:w-auto'
                        variant='outlined'
                        onClick={() => {
                          if (selectedUser)
                            handleSubUserSendEmail(
                              user?.sub_id as number,
                              user?.sub_email as string,
                            )
                              .then(() => {})
                              .catch(() => {})
                        }}
                      >
                        <Trans id='Re-send Invite' />
                      </Button>
                    </div>
                  </div>
                )
              }
              return null
            })}
          </div>
        )}
      </div>
      <div className='mt-11 flex w-full flex-col items-center justify-center gap-[30px] md:flex-row md:justify-start'>
        <StandardButton
          type='button'
          className='w-full max-w-[350px] bg-material-ui-blue uppercase md:w-auto'
          variant='contained'
          onClick={() => {
            setSelectedRole(null)
            setShowForm(true)
          }}
          size='small'
        >
          {i18n._('Add New User')}
        </StandardButton>
        {/* <StandardButton
          type='button'
          className='max-w-[350px] bg-material-ui-blue uppercase'
          variant='contained'
          disabled={!selectedRole}
          onClick={() => setIsDisplayWarning(true)}
        >
          {i18n._('Delete')}
        </StandardButton> */}
      </div>
      <Modal onClose={() => setShowForm(false)} open={!!showForm} className='z-10 '>
        <div className='absolute left-[5vw] top-[5vh] w-[90vw] overflow-auto rounded-md border-solid bg-pure-white lg:left-[20vw] lg:w-[60vw]'>
          {selectedRole && users && users.find((user) => user?.sub_id === selectedRole) ? (
            <div className='p-10 px-10 py-6'>
              <SectionTitle title='Add new user' />

              <SubUserForm
                user={
                  users.find((user) => user?.sub_id === selectedRole) as unknown as SubUserFragment
                }
                onSuccess={(mode, userEmail) => {
                  setSubUserEmail(userEmail as string)
                  setShowForm(false)
                  refetch()
                    .then(({ data }) => {
                      // Send Email to newly creatd sub-user
                      if (mode === 'NEW') {
                        const subUsers = data.customer?.sub_users
                        const subUser = subUsers
                          ? subUsers.find((subU) => subU?.sub_email === userEmail)
                          : false
                        if (subUser && subUser.sub_id) {
                          handleSubUserSendEmail(subUser.sub_id, userEmail as string, false)
                            .then(() => {})
                            .catch(() => {})
                        }
                      }
                    })
                    .catch(() => {})
                }}
                onCancel={() => setShowForm(false)}
              />
            </div>
          ) : null}
          {!selectedRole && (
            <div className='p-10 px-10 py-6'>
              <SectionTitle title='Add new user' />

              <SubUserForm
                onSuccess={(mode, userEmail) => {
                  setSubUserEmail(userEmail as string)
                  setShowForm(false)
                  refetch()
                    .then(({ data }) => {
                      // Send Email to newly creatd sub-user
                      if (mode === 'NEW') {
                        const subUsers = data.customer?.sub_users
                        const subUser = subUsers
                          ? subUsers.find((subU) => subU?.sub_email === userEmail)
                          : false
                        if (subUser && subUser.sub_id) {
                          handleSubUserSendEmail(subUser.sub_id, userEmail as string, false)
                            .then(() => {})
                            .catch(() => {})
                        }
                      }
                    })
                    .catch(() => {})
                }}
                onCancel={() => setShowForm(false)}
              />
            </div>
          )}
        </div>
      </Modal>
      <AlertPrompt
        title={i18n._('Email Sent')}
        message={i18n._(`Email has been sent to ${subUserEmail}!`, { subUserEmail })}
        isDisplayed={displayPrompt}
        setIsDisplayed={setDisplayPrompt}
        callback={handleClosePrompt}
      />
      <ConfirmationPrompt
        title={i18n._('Remove User')}
        message={i18n._(
          'Do you want to remove this user from your company account? Please take note that this action will send an email notification to the removed user.',
        )}
        isDisplayed={displayWarning}
        setIsDisplayed={setIsDisplayWarning}
        setResponse={setResponseDelete}
        callback={handleSubUserDelete}
        callbackArgs={selectedRole as number}
      />
    </Paper>
  )
}
